import Footer from './Footer';

import {useEffect, useLayoutEffect, useRef} from 'react';
import {Helmet} from 'react-helmet';
import {Link} from 'react-router-dom';

const TABLEAU_SCRIPT_URL =
  'https://public.tableau.com/javascripts/api/viz_v1.js';
const TABLEAU_DASHBOARD_WIDTH = 560;
const TABLEAU_DASHBOARD_HEIGHT = 4927;

function PoliceKillingsArchive() {
  const tableauContainer = useRef(null);
  const tableauViewport = useRef(null);

  useLayoutEffect(() => {
    const updateTableauScale = () => {
      const availableWidth = tableauViewport.current?.clientWidth;
      if (!availableWidth || !tableauContainer.current) return;

      const scale = Math.min(1, availableWidth / TABLEAU_DASHBOARD_WIDTH);
      tableauContainer.current.style.transform = `scale(${scale})`;
      tableauViewport.current.style.height = `${
        TABLEAU_DASHBOARD_HEIGHT * scale
      }px`;
    };

    updateTableauScale();
    window.addEventListener('resize', updateTableauScale);

    const resizeObserver = window.ResizeObserver
      ? new window.ResizeObserver(updateTableauScale)
      : null;
    if (resizeObserver && tableauViewport.current) {
      resizeObserver.observe(tableauViewport.current);
    }

    return () => {
      window.removeEventListener('resize', updateTableauScale);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    const vizElement = tableauContainer.current?.querySelector('object');
    if (!vizElement) return undefined;

    const scriptElement = document.createElement('script');
    scriptElement.async = true;
    scriptElement.src = TABLEAU_SCRIPT_URL;
    vizElement.parentNode.insertBefore(scriptElement, vizElement);

    window.scrollTo(0, 0);

    return () => scriptElement.remove();
  }, []);

  return (
    <>
      <Helmet>
        <title>Police Killings Data Archive</title>
        <meta name="title" content="Police Killings Data Archive" />
        <meta
          name="description"
          content="Access comprehensive data and analysis of over 15,000 killings by police in the United States. By the Police Data Archive."
        />
      </Helmet>

      <main className="PoliceKillingsArchive">
        <div className="archive-copy fadeInUp" style={{animationDelay: `0.5s`}}>
          <h1 className="title">Police Killings Data Archive</h1>

          <p>
            This page is a permanent archive of this organization&apos;s initial
            database documenting fatal police violence in the United States, the
            culmination of over a decade of research and data collection led by
            our founder, Samuel Sinyangwe. This includes detailed information
            about more than 15,000 cases of police killings nationwide between
            2013-2026, representing the most comprehensive and methodologically
            consistent accounting of fatal police violence in America during
            this period.
          </p>

          <p>
            As we expand our data collection and analysis to account for{' '}
            <Link to="/">all forms of police violence</Link>, we are archiving
            our past work focused on fatal police violence here so that it can
            continue to be used as a resource by organizers, journalists and
            academics. Use the maps and charts below to explore our data on
            fatal police violence or{' '}
            <a
              href="https://docs.google.com/spreadsheets/d/19URywaaBdt840FlRp8srrVtAy4lKMlYF/edit?usp=sharing&ouid=113333663773593806856&rtpof=true&sd=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>{' '}
            to download the database.
          </p>
        </div>

        <div className="archive-visualization carto-visualization">
          <iframe
            className="external-visualization police-killings-map"
            title="Map of police killings in the United States"
            src="https://samswey.carto.com/viz/e1005588-93d2-47b6-9a61-f4c0ed001163/embed_map"
            width="100%"
            height="520"
            frameBorder="0"
            loading="lazy"
            allowFullScreen
          />
        </div>

        <div
          className="archive-visualization tableau-visualization"
          ref={tableauViewport}
          style={{height: `${TABLEAU_DASHBOARD_HEIGHT}px`}}
        >
          <div
            className="tableauPlaceholder"
            id="viz1787423475597"
            ref={tableauContainer}
            style={{
              height: `${TABLEAU_DASHBOARD_HEIGHT}px`,
              position: 'relative',
              transform: 'scale(1)',
              transformOrigin: 'top center',
              width: `${TABLEAU_DASHBOARD_WIDTH}px`,
            }}
          >
            <object
              aria-label="Police Killings Dashboard"
              className="tableauViz"
              style={{
                display: 'none',
                height: `${TABLEAU_DASHBOARD_HEIGHT}px`,
                width: `${TABLEAU_DASHBOARD_WIDTH}px`,
              }}
            >
              <param
                name="host_url"
                value="https%3A%2F%2Fpublic.tableau.com%2F"
              />
              <param name="embed_code_version" value="3" />
              <param name="site_root" value="" />
              <param
                name="name"
                value="PoliceKillingsDashboard_17874233912750&#47;PoliceKillingsDashboard"
              />
              <param name="tabs" value="no" />
              <param name="toolbar" value="yes" />
              <param
                name="static_image"
                value="https:&#47;&#47;public.tableau.com&#47;static&#47;images&#47;Po&#47;PoliceKillingsDashboard_17874233912750&#47;PoliceKillingsDashboard&#47;1.png"
              />
              <param name="animate_transition" value="yes" />
              <param name="display_static_image" value="yes" />
              <param name="display_spinner" value="yes" />
              <param name="display_overlay" value="yes" />
              <param name="display_count" value="yes" />
              <param name="language" value="en-US" />
              <param name="filter" value="publish=yes" />
            </object>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}

export default PoliceKillingsArchive;
